import { useTranslation } from 'react-i18next'
import React, { useEffect } from 'react'
import {
  Document,
  Image,
  Link,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer'
import moment from 'moment'

import '../../lib/pdfFonts'
import {
  CivicRushMonogram,
  CivicRushWordmark,
} from '../../components/pdf/CivicRushLogo'
import { isVideo } from '../../lib/files'
import {
  sortCategoriesAlphabetically,
  sortObjectsAlphabetically,
} from '../../lib/sort'
import { getTeam, removeHTMLTags } from '../../lib/event'
import { formatLocation } from '../../lib/address'

const PURPLE = '#66308D'
const PURPLE_DARK = '#4D2270'
const PURPLE_LIGHT = '#8B5BAF'
const PINK = '#E6526D'
const DARK = '#4A4A4A'
const MID = '#767676'
const LIGHT = '#9B9B9B'
const CARD_BG = '#F4F2F7'
const GOLD = '#C8922A'
const GREEN = '#61A913'

const HEART_PATH =
  'M12 21s-6.7-4.3-9.3-8.4A5.4 5.4 0 0112 6.6a5.4 5.4 0 019.3 6C18.7 16.7 12 21 12 21z'

const CalendarIcon = () => (
  <Svg width={11} height={11} viewBox="0 0 24 24">
    <Path
      d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zm0 16H5V10h14v10zM5 8V6h14v2H5z"
      fill={PINK}
    />
  </Svg>
)

const PinIcon = () => (
  <Svg width={11} height={11} viewBox="0 0 24 24">
    <Path
      d="M12 2a7 7 0 00-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 00-7-7zm0 9.5A2.5 2.5 0 1114.5 9 2.5 2.5 0 0112 11.5z"
      fill={PINK}
    />
  </Svg>
)

const CheckIcon = () => (
  <Svg width={11} height={11} viewBox="0 0 24 24">
    <Path
      d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm-1.2 14.5l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4z"
      fill={GREEN}
    />
  </Svg>
)

// fa-hearts (duotone style): small secondary heart upper right, solid primary lower left
const HeartsIcon = () => (
  <Svg width={28} height={26} viewBox="0 0 26 24">
    <Path
      d={HEART_PATH}
      fill={PINK}
      opacity={0.45}
      transform="translate(11.5, -1.2) scale(0.58)"
    />
    <Path
      d={HEART_PATH}
      fill={PINK}
      transform="translate(0, 4.5) scale(0.82)"
    />
  </Svg>
)

// no `body` key: Chrome rejects GET requests with a body, which silently
// dropped every remote image from the PDF
const remoteImageSrc = uri => ({
  uri,
  method: 'GET',
  headers: { 'Cache-Control': 'no-cache' },
})

const titleFontSize = title => {
  const length = title?.length ?? 0
  if (length > 70) return 19
  if (length > 42) return 23
  return 28
}

export default function EventFlyer({
  event,
  acceptedPledges,
  fileName,
  qr,
  businessQr,
  locale,
}) {
  const { i18n, t } = useTranslation()

  const firstAttachment = event.media_attachments?.[0]
  const mainImage = firstAttachment
    ? isVideo(firstAttachment)
      ? firstAttachment.thumbnail_url
      : firstAttachment.direct_upload_url
    : null
  const team = getTeam(event)

  const startMoment = moment(event?.event_start_date || undefined).tz(
    moment.tz.guess(),
  )
  const endMoment = (
    event?.event_end_date ? moment(event.event_end_date) : moment().add(1, 'h')
  ).tz(moment.tz.guess())
  const sameDay = startMoment.isSame(endMoment, 'day')
  const formattedLocation = formatLocation(event.location)

  // pledges arrive sorted by amount (files.js); pilot shows one exclusive champion
  const champion = acceptedPledges?.[0]?.business
  const isSpanish =
    locale === 'es' || localStorage.getItem('i18nextLng') === 'es'

  useEffect(() => {
    moment.locale(locale)
    i18n.changeLanguage(locale)
  }, [locale])

  const eventUrl = `${window.location.origin}/events/${event.slug || event.id}`

  return (
    <Document title={fileName}>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.masthead}>
          <View style={styles.lockup}>
            <CivicRushMonogram height={30} fill={PURPLE} />
            <CivicRushWordmark height={14} fill={PURPLE} />
          </View>
          <Text style={styles.tagline}>{t('flyer.tagline')}</Text>
        </View>

        {!!mainImage && (
          <Image style={styles.hero} src={remoteImageSrc(mainImage)} />
        )}

        <View style={styles.titleBlock}>
          <Text
            style={[styles.title, { fontSize: titleFontSize(event.title) }]}
          >
            {event.title}
          </Text>
        </View>

        <View style={styles.bodyCols}>
          <View style={styles.colL}>
            <View style={styles.whenWhere}>
              {event.event_type !== 'need' && (
                <View style={styles.metaItem}>
                  <View style={styles.metaIcon}>
                    <CalendarIcon />
                  </View>
                  <Text style={styles.metaText}>
                    {startMoment.format(t('config.date_format'))}
                    {!sameDay &&
                      ` ${t('event.to')} ${endMoment.format(
                        t('config.date_format'),
                      )}`}
                    {'   ·   '}
                    {startMoment.format('h:mm a')} {t('event.to')}{' '}
                    {endMoment.format('h:mm a z')}
                  </Text>
                </View>
              )}
              <View style={styles.metaItem}>
                <View style={styles.metaIcon}>
                  <PinIcon />
                </View>
                <Text style={styles.metaText}>
                  {!!event.remote
                    ? t('event.remote')
                    : !!formattedLocation
                      ? formattedLocation
                      : t('event.locationDefault')}
                </Text>
              </View>
            </View>

            <Text style={styles.secLabel}>{t('flyer.about')}</Text>
            <Text style={styles.body}>
              {removeHTMLTags(event.details ?? '')}
            </Text>

            {!!event.volunteers_details && (
              <>
                <Text style={styles.secLabel}>{t('event.volunteer_info')}</Text>
                <Text style={styles.body}>
                  {removeHTMLTags(event.volunteers_details)}
                </Text>
              </>
            )}

            <Text style={styles.orgLine}>
              {t('flyer.organized_by')}{' '}
              <Text style={styles.orgName}>{event.user?.name}</Text>
            </Text>
            {!!team.length && (
              <Text style={styles.orgLine}>
                {t('event.team')}{' '}
                {sortObjectsAlphabetically(team, 'name')
                  .map(member => member.name)
                  .join(', ')}
              </Text>
            )}
            {!!event.groups?.length && (
              <Text style={styles.orgLine}>
                {t('event.private_groups')}{' '}
                {sortObjectsAlphabetically([...event.groups], 'name')
                  .map(group => group.name)
                  .join(', ')}
              </Text>
            )}
            {!!event.hashtags && (
              <Text style={styles.hashtags}>{event.hashtags}</Text>
            )}

            <View style={styles.pills}>
              {!!event.categories?.length ? (
                sortCategoriesAlphabetically(event.categories, locale).map(
                  category => (
                    <View style={styles.pill} key={category.id}>
                      <Text>
                        {isSpanish ? category.es_name : category.en_name}
                      </Text>
                    </View>
                  ),
                )
              ) : (
                <View style={styles.pill}>
                  <Text>
                    {isSpanish
                      ? 'Mejorar la Comunidad'
                      : 'Community Improvement'}
                  </Text>
                </View>
              )}
            </View>
          </View>

          <View style={styles.colR}>
            <View style={styles.qrCard}>
              <View style={styles.qrTile}>
                <Image style={styles.qrImg} src={qr} />
              </View>
              <Text style={styles.scan}>{t('flyer.scan_cta')}</Text>
              <Text style={styles.scanSub}>{t('flyer.scan_sub')}</Text>
              {!!event.service_hours && (
                <View style={styles.svcChip}>
                  <CheckIcon />
                  <Text style={styles.svcText}>
                    {t('flyer.certified_hours')}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {!!champion && (
          <>
            <View style={styles.pfBand}>
              <HeartsIcon />
              <View style={styles.pfText}>
                <Text style={styles.pfHead}>{t('flyer.pay_forward')}</Text>
                <Text style={styles.pfCopy}>{t('flyer.pay_forward_copy')}</Text>
              </View>
            </View>

            <View style={styles.sponsors}>
              <View style={styles.spHeader}>
                <Text style={styles.spLabel}>{t('flyer.champion')}</Text>
                <View style={styles.spRule} />
              </View>
              <View style={styles.champRow}>
                <Image
                  style={styles.champAvatar}
                  src={remoteImageSrc(
                    champion.media_attachment?.direct_upload_url ||
                      '/images/default-business.png',
                  )}
                />
                <View style={styles.champMain}>
                  <Text style={styles.champName}>{champion.name}</Text>
                  {!!champion.pay_forward_message && (
                    <Text style={styles.champQuote}>
                      “{champion.pay_forward_message}”
                    </Text>
                  )}
                </View>
                {!!businessQr && (
                  <View style={styles.champQr}>
                    <Image style={styles.champQrImg} src={businessQr} />
                    <Text style={styles.champQrLabel}>
                      {t('flyer.champion_qr')}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </>
        )}

        <View style={styles.footer}>
          <Link style={styles.footerUrl} src={eventUrl}>
            {eventUrl.replace(/^https?:\/\//, '')}
          </Link>
          <Text style={styles.footerTag}>{t('flyer.tagline')}</Text>
        </View>
      </Page>
    </Document>
  )
}

// Letter is 612x792pt; the approved mock is 816x1056px, so mock px × 0.75 = pt
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Roboto',
    fontSize: 10.5,
    color: DARK,
    display: 'flex',
    flexDirection: 'column',
  },
  masthead: {
    height: 54,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 33,
    borderBottomWidth: 2.25,
    borderBottomColor: PINK,
  },
  lockup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tagline: {
    fontFamily: 'RobotoItalic',
    fontSize: 11,
    color: PURPLE,
  },
  hero: {
    height: 111,
    marginTop: 10.5,
    marginHorizontal: 33,
    borderRadius: 10.5,
    objectFit: 'cover',
  },
  titleBlock: {
    paddingTop: 10.5,
    paddingHorizontal: 33,
    paddingBottom: 3,
  },
  title: {
    fontFamily: 'RobotoCondensed',
    color: PURPLE,
    lineHeight: 1.05,
    letterSpacing: 0.2,
  },
  bodyCols: {
    flexDirection: 'row',
    gap: 22.5,
    paddingTop: 9,
    paddingHorizontal: 33,
    flexGrow: 1,
    flexShrink: 1,
  },
  colL: {
    // flexBasis 0 keeps long unbroken content wrapping inside the column
    // instead of widening it into the QR rail
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
  colR: {
    width: 192,
    flexGrow: 0,
    flexShrink: 0,
  },
  whenWhere: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    marginBottom: 9,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  metaIcon: {
    marginTop: 1.5,
  },
  metaText: {
    fontSize: 11.25,
    fontWeight: 700,
    color: DARK,
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
  secLabel: {
    fontFamily: 'RobotoCondensed',
    fontSize: 9.75,
    letterSpacing: 1.35,
    color: PURPLE_LIGHT,
    textTransform: 'uppercase',
    marginBottom: 4.5,
  },
  body: {
    fontSize: 10.5,
    lineHeight: 1.65,
    color: DARK,
    marginBottom: 7.5,
  },
  orgLine: {
    fontSize: 10,
    marginBottom: 3,
  },
  orgName: {
    fontWeight: 700,
    color: PURPLE,
  },
  hashtags: {
    fontSize: 9.75,
    color: PINK,
    marginTop: 2,
    marginBottom: 6,
  },
  pills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
    marginTop: 2,
  },
  pill: {
    borderWidth: 1.1,
    borderColor: PINK,
    borderRadius: 4.5,
    backgroundColor: '#FFFFFF',
    fontSize: 8.6,
    color: DARK,
    paddingVertical: 2.25,
    paddingHorizontal: 7.5,
  },
  qrCard: {
    backgroundColor: PURPLE_LIGHT,
    borderRadius: 10.5,
    padding: 10.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 7.5,
  },
  qrTile: {
    backgroundColor: '#FFFFFF',
    borderRadius: 7.5,
    padding: 7.5,
  },
  qrImg: {
    width: '100%',
  },
  scan: {
    fontFamily: 'RobotoCondensed',
    fontSize: 15.75,
    letterSpacing: 0.9,
    color: '#FFFFFF',
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  scanSub: {
    fontSize: 9.75,
    color: 'rgba(255, 255, 255, 0.94)',
    lineHeight: 1.45,
    textAlign: 'center',
    marginTop: -3,
  },
  svcChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 7.5,
  },
  svcText: {
    fontSize: 9,
    fontWeight: 700,
    color: GREEN,
    flexShrink: 1,
    textAlign: 'center',
  },
  pfBand: {
    marginTop: 9,
    marginHorizontal: 33,
    backgroundColor: CARD_BG,
    borderLeftWidth: 4.5,
    borderLeftColor: PINK,
    borderRadius: 6,
    paddingVertical: 10.5,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  pfText: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
  pfHead: {
    fontFamily: 'RobotoCondensed',
    fontSize: 14.25,
    letterSpacing: 1.2,
    color: PINK,
    textTransform: 'uppercase',
  },
  pfCopy: {
    fontSize: 10,
    lineHeight: 1.5,
    color: DARK,
    marginTop: 1.5,
  },
  sponsors: {
    paddingTop: 19.5,
    paddingBottom: 21,
    paddingHorizontal: 33,
  },
  spHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 12,
  },
  spLabel: {
    fontFamily: 'RobotoCondensed',
    fontSize: 9.75,
    letterSpacing: 1.65,
    color: GOLD,
    textTransform: 'uppercase',
  },
  spRule: {
    flexGrow: 1,
    height: 1.1,
    backgroundColor: LIGHT,
    opacity: 0.35,
  },
  champRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13.5,
  },
  champAvatar: {
    width: 43.5,
    height: 43.5,
    borderRadius: 21.75,
    borderWidth: 1.5,
    borderColor: CARD_BG,
    objectFit: 'cover',
  },
  champMain: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
  },
  champName: {
    fontSize: 13.5,
    fontWeight: 700,
    color: DARK,
  },
  champQuote: {
    fontFamily: 'RobotoItalic',
    fontSize: 10,
    color: MID,
    lineHeight: 1.45,
    marginTop: 2,
  },
  champQr: {
    alignItems: 'center',
  },
  champQrImg: {
    width: 46.5,
    height: 46.5,
  },
  champQrLabel: {
    fontSize: 7.1,
    color: MID,
    marginTop: 2,
    textAlign: 'center',
  },
  footer: {
    marginTop: 'auto',
    borderTopWidth: 2.25,
    borderTopColor: PINK,
    paddingVertical: 12,
    paddingHorizontal: 33,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  footerUrl: {
    fontSize: 9.4,
    fontWeight: 700,
    color: PURPLE,
    textDecoration: 'none',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 12,
  },
  footerTag: {
    fontFamily: 'RobotoItalic',
    fontSize: 12.75,
    color: PURPLE,
    flexShrink: 0,
    textAlign: 'right',
  },
})
